import { format } from "date-fns";

import { pad2 } from "@/utils/command-scheduler";

function cadenceFromScheduleType(job: ScheduledJob): "once" | "daily" | "weekly" | "monthly" {
    const type = (job.schedule_type ?? "").toLowerCase();
    const hasCron = Boolean((job.cron_expression ?? "").trim());
    if (type !== "cron" && !hasCron) return "once";
    const parts = (job.cron_expression ?? "").trim().split(/\s+/);
    if (parts.length >= 5) {
        const dayOfMonth = parts[2];
        const dayOfWeek = parts[4];
        if (dayOfMonth && dayOfMonth !== "*") return "monthly";
        if (dayOfWeek && dayOfWeek !== "*") {
            const days = dayOfWeek
                .split(",")
                .map((value) => Number.parseInt(value, 10))
                .filter(Number.isFinite);
            return days.length === 1 ? "weekly" : "daily";
        }
    }
    return "daily";
}

function parseUtcInstant(value: string): Date | null {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed);
    const date = new Date(hasZone ? trimmed : `${trimmed}Z`);
    if (Number.isNaN(date.getTime())) return null;
    return date;
}

function parseCronLocalHm(cron: string | null | undefined): string | null {
    const parts = (cron ?? "").trim().split(/\s+/);
    if (parts.length < 2) return null;
    const minute = Number.parseInt(parts[0] ?? "", 10);
    const hour = Number.parseInt(parts[1] ?? "", 10);
    if (!Number.isFinite(minute) || !Number.isFinite(hour)) return null;
    const now = new Date();
    const local = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hour, minute, 0, 0)
    );
    return `${pad2(local.getHours())}:${pad2(local.getMinutes())}`;
}

function scheduleTimeHm(job: ScheduledJob): string {
    const fromCron = parseCronLocalHm(job.cron_expression);
    if (fromCron) return fromCron;
    const instant =
        parseUtcInstant(job.next_run_at ?? "") ??
        parseUtcInstant(job.run_at ?? "") ??
        parseUtcInstant(job.last_run_at ?? "");
    return instant ? format(instant, "HH:mm") : "—";
}

export function mapScheduleJobToRow(job: ScheduledJob) {
    const nextRunDate = job.next_run_at ? parseUtcInstant(job.next_run_at) : null;
    return {
        id: job.id,
        title: job.description?.trim() || job.job_name?.trim() || "Scheduled prompt",
        cadenceLabel: cadenceFromScheduleType(job),
        time: scheduleTimeHm(job),
        agentId: job.entity_id ?? null,
        createdAt: job.created_at ?? job.updated_at ?? new Date().toISOString(),
        nextRunAt: job.next_run_at ?? null,
        nextRunLabel: nextRunDate
            ? format(nextRunDate, "MMM d, yyyy 'at' h:mm a")
            : null,
        executionStatus: job.execution_status ?? "",
        runCount: job.run_count ?? 0,
    };
}
